import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './sshSecurityInfo.api';
import { getSelectedSshSecurityInfos } from './sshSecurityInfo.selectors';
import {
  SshSecurityInfo,
  SshSecurityInfoActionTypes,
  CreateSshSecurityInfoThunkAction,
  UpdateSshSecurityInfoThunkAction,
  CREATE_SSH_SECURITY_INFOS,
  GET_SSH_SECURITY_INFO,
  GET_SSH_SECURITY_INFOS,
  GET_SSH_SECURITY_INFOS_LIST,
  SetSshSecurityInfoLoadingAction,
  GO_TO_SSH_SECURITY_INFOS_CREATE,
  GO_TO_SSH_SECURITY_INFOS_EDIT,
  GO_TO_SSH_SECURITY_INFOS_VIEW,
  GO_TO_SSH_SECURITY_INFOS_LIST,
  SelectSshSecurityInfoAction,
  SET_SSH_SECURITY_INFOS_LOADING,
  SELECT_SSH_SECURITY_INFOS,
  UPDATE_SSH_SECURITY_INFOS,
  DELETE_SSH_SECURITY_INFOS,
  DeleteSshSecurityInfoThunkAction,
} from './sshSecurityInfo.types';
import { SSH_SECURITY_INFORMATION, SYSTEM_CONFIGURATION_ROUTE, SECURITY } from '../../constants';

export function goToSshSecurityInfoList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SSH_SECURITY_INFOS_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SSH_SECURITY_INFORMATION}`);
  };
}

export function goToSshSecurityInfoCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SSH_SECURITY_INFOS_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SSH_SECURITY_INFORMATION}/create`);
  };
}

export function goToSshSecurityInfoEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SSH_SECURITY_INFOS_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SSH_SECURITY_INFORMATION}/edit`);
  };
}

export function goToSshSecurityInfoView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_SSH_SECURITY_INFOS_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SSH_SECURITY_INFORMATION}/view`);
  };
}

export function setSshSecurityInfoLoadingAction(
  isLoading: boolean,
): SetSshSecurityInfoLoadingAction {
  return {
    type: SET_SSH_SECURITY_INFOS_LOADING,
    payload: { isLoading },
  };
}

export function getSshSecurityInfoList(deliveryFunctionId: string): SshSecurityInfoActionTypes {
  return {
    type: GET_SSH_SECURITY_INFOS_LIST,
    payload: api.getSshSecurityInfosList(deliveryFunctionId),
  };
}

export function getSshSecurityInfos(
  deliveryFunctionId: string,
): SshSecurityInfoActionTypes {
  return {
    type: GET_SSH_SECURITY_INFOS,
    payload: api.getSshSecurityInfos(deliveryFunctionId),
  };
}

export function getSshSecurityInfo(
  deliveryFunctionId: string, id: string,
): SshSecurityInfoActionTypes {
  return {
    type: GET_SSH_SECURITY_INFO,
    payload: api.getSshSecurityInfo(deliveryFunctionId, id),
  };
}

export function createSshSecurityInfo(
  deliveryFunctionId: string,
  body: WithAuditDetails<SshSecurityInfo>,
  history: History,
): CreateSshSecurityInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_SSH_SECURITY_INFOS,
        payload: api.createSshSecurityInfos(deliveryFunctionId, body),
      });

      dispatch(goToSshSecurityInfoList(history));
      showSuccessMessage(i18n.t('sshSecurityInfo.create.success', { id: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sshSecurityInfo.create.failed', { id: body.data?.id, error: errorMessage }));
    }
  };
}

export function createSshSecurityInfoFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<SshSecurityInfo>,
): CreateSshSecurityInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setSshSecurityInfoLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_SSH_SECURITY_INFOS,
        payload: api.createSshSecurityInfos(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('sshSecurityInfo.create.success', { id: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sshSecurityInfo.create.failed', { id: body.data?.id, error: errorMessage }));

    } finally {
      dispatch(setSshSecurityInfoLoadingAction(false));
    }
  };
}

export function updateSshSecurityInfo(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<SshSecurityInfo>,
  history: History,
): UpdateSshSecurityInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_SSH_SECURITY_INFOS,
        payload: api.updateSshSecurityInfos(deliveryFunctionId, id, body),
      });

      dispatch(goToSshSecurityInfoList(history));
      showSuccessMessage(i18n.t('sshSecurityInfo.update.success', { id: body.data?.id }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('sshSecurityInfo.update.failed', { id: body.data?.id, error: errorMessage }));
    }
  };
}

export function selectSshSecurityInfo(
  sshSecurityInfo: SshSecurityInfo[],
): SelectSshSecurityInfoAction {
  return {
    type: SELECT_SSH_SECURITY_INFOS,
    payload: sshSecurityInfo,
  };
}

export function deleteSshSecurityInfo(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteSshSecurityInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedSshSecurityInfo] = getSelectedSshSecurityInfos(getState());
    try {
      await dispatch({
        type: DELETE_SSH_SECURITY_INFOS,
        payload: api.deleteSshSecurityInfo(deliveryFunctionId, id, body),
      });
      showSuccessMessage(i18n.t('sshSecurityInfo.delete.success', { name: selectedSshSecurityInfo?.SS8ID }), 5000);
      if (history != null) {
        dispatch(goToSshSecurityInfoList(history));
      }
    } catch (e) {
      showError(i18n.t('sshSecurityInfo.delete.failed', { name: selectedSshSecurityInfo?.SS8ID }));
    } finally {
      dispatch(getSshSecurityInfoList(deliveryFunctionId));
    }
  };
}
